/**
 * Apartments table — floor filter + column sorting.
 *
 * Every apartment is rendered by PHP; this only reorders and hides rows,
 * so there is no request on interaction.
 */

const STATUS_WEIGHT = { available: 0, reserved: 1, sold: 2 };

const ApartmentsTable = {
  init() {
    document.querySelectorAll(".js-apartments").forEach((root) => setup(root));
  },
};

function setup(root) {
  const body = root.querySelector(".js-apartments-body");

  if (!body) {
    return;
  }

  const rows = Array.from(body.querySelectorAll(".js-apartments-row"));
  const sortButtons = Array.from(root.querySelectorAll(".js-apartments-sort"));
  const floorButtons = Array.from(root.querySelectorAll(".js-apartments-floor"));
  const buildingButtons = Array.from(
    root.querySelectorAll(".js-apartments-building")
  );

  // The initially active floor tab is marked server-side (first floor by
  // default); fall back to showing everything when no tab carries it.
  const initialFloor = floorButtons.find((b) =>
    b.classList.contains("is-active")
  );

  const state = {
    building: "all",
    floor: initialFloor ? initialFloor.dataset.floor : "all",
    sort: "number",
    direction: "asc",
  };

  // ---------------------------------------------------------------- sorting
  function valueOf(row, key) {
    if (key === "status") {
      return STATUS_WEIGHT[row.dataset.status] ?? 0;
    }

    const raw = row.dataset[key];
    const num = parseFloat(raw);

    return Number.isNaN(num) ? 0 : num;
  }

  function applySort() {
    const factor = state.direction === "asc" ? 1 : -1;

    const sorted = rows.slice().sort((a, b) => {
      const diff = valueOf(a, state.sort) - valueOf(b, state.sort);

      // Keep a stable, predictable secondary order.
      return diff !== 0
        ? diff * factor
        : valueOf(a, "number") - valueOf(b, "number");
    });

    const fragment = document.createDocumentFragment();
    sorted.forEach((row) => fragment.appendChild(row));
    body.appendChild(fragment);

    sortButtons.forEach((button) => {
      const isActive = button.dataset.sort === state.sort;

      button.classList.toggle("is-sorted", isActive);
      button.classList.toggle("is-desc", isActive && state.direction === "desc");
      button.setAttribute(
        "aria-sort",
        isActive
          ? state.direction === "asc"
            ? "ascending"
            : "descending"
          : "none"
      );
    });
  }

  // -------------------------------------------------------------- filtering
  function matches(row) {
    const buildingOk =
      state.building === "all" || row.dataset.building === state.building;
    const floorOk = state.floor === "all" || row.dataset.floor === state.floor;

    return buildingOk && floorOk;
  }

  /**
   * A floor tab is only offered when the selected building actually has
   * apartments on that floor.
   */
  function syncFloorTabs() {
    floorButtons.forEach((button) => {
      if (button.dataset.floor === "all") {
        return;
      }

      const buildings = (button.dataset.buildings || "")
        .split(",")
        .filter(Boolean);

      const available =
        state.building === "all" || buildings.indexOf(state.building) !== -1;

      button.hidden = !available;

      // The active floor just disappeared — fall back to showing everything.
      if (!available && state.floor === button.dataset.floor) {
        state.floor = "all";
      }
    });
  }

  function applyFilter() {
    syncFloorTabs();

    rows.forEach((row) => {
      const visible = matches(row);
      row.hidden = !visible;
      row.classList.toggle("is-hidden", !visible);
    });

    floorButtons.forEach((button) => {
      const isActive = button.dataset.floor === state.floor;

      button.classList.toggle("is-active", isActive);
      button.setAttribute("aria-pressed", isActive ? "true" : "false");
    });

    buildingButtons.forEach((button) => {
      const isActive = button.dataset.building === state.building;

      button.classList.toggle("is-active", isActive);
      button.setAttribute("aria-pressed", isActive ? "true" : "false");
    });
  }

  // ----------------------------------------------------------------- events
  sortButtons.forEach((button) => {
    button.addEventListener("click", () => {
      const key = button.dataset.sort;

      if (state.sort === key) {
        state.direction = state.direction === "asc" ? "desc" : "asc";
      } else {
        state.sort = key;
        state.direction = "asc";
      }

      applySort();
    });
  });

  floorButtons.forEach((button) => {
    button.addEventListener("click", () => {
      state.floor = button.dataset.floor;
      applyFilter();
    });
  });

  buildingButtons.forEach((button) => {
    button.addEventListener("click", () => {
      state.building = button.dataset.building;
      state.floor = "all";
      applyFilter();
    });
  });

  applySort();
  applyFilter();
}

export default ApartmentsTable;
